import React, { useEffect, useState } from 'react';
import { FaPen, FaClipboardList } from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCampaign, fetchTemplate } from '../api/api';

const CampaignDetail = ({ setAlert }) => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // templateName -> { questionCount, language } fetched per linked scorecard.
  // The campaign endpoint only returns scorecard names; the counts are what
  // make this page worth visiting, so they load after the campaign shell.
  const [details, setDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchCampaign(name);
        setCampaign(res);

        const templates = res?.templates || [];
        if (templates.length > 0) {
          setDetailsLoading(true);
          // Each fetch fails independently — a tile without a count is
          // better than no tiles at all.
          const results = await Promise.all(
            templates.map((t) =>
              fetchTemplate(t)
                .then((tpl) => [t, {
                  questionCount: (tpl.questions || []).length,
                  language: tpl.language || null,
                }])
                .catch(() => [t, null])
            )
          );
          setDetails(Object.fromEntries(results));
          setDetailsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load campaign', err);
        setError('Failed to load campaign.');
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaign.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [name, setAlert]);

  if (loading) {
    return <LoadingState variant="detail" label="Loading campaign" />;
  }

  if (error) {
    return <div className="text-center mt-20 text-red-500">{error}</div>;
  }

  if (!campaign) {
    return <div className="text-center mt-20 text-gray-500">Campaign not found.</div>;
  }

  const templates = campaign.templates || [];
  const isActive = campaign.active === true || campaign.active === undefined;
  const totalQuestions = Object.values(details).reduce(
    (sum, d) => (d ? sum + d.questionCount : sum),
    0
  );

  const meta = [
    campaign.version && { label: 'Version', value: campaign.version },
    campaign.createdAt && { label: 'Created', value: new Date(campaign.createdAt).toLocaleString() },
    campaign.updatedAt && { label: 'Last updated', value: new Date(campaign.updatedAt).toLocaleString() },
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-lg font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
              {campaign.campaignName.trim().charAt(0)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight truncate">
                  {campaign.campaignName}
                </h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isActive ? 'bg-coral-50 text-coral-700' : 'bg-gray-100 text-gray-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-coral-500' : 'bg-gray-400'}`} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-gray-500 text-sm mt-1">Campaign details and linked scorecards</p>
              {meta.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {meta.map(({ label, value }) => (
                    <span key={label} className="text-xs text-gray-400">
                      <span className="font-semibold text-gray-500">{label}:</span> {value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate(`/campaigns/${encodeURIComponent(campaign.campaignName)}`)}
            className="btn-primary flex-shrink-0"
          >
            <FaPen /> Edit
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Linked scorecards</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{templates.length}</div>
        </div>
        <div className="card">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total questions</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {detailsLoading ? <span className="text-gray-300">…</span> : totalQuestions}
          </div>
        </div>
        <div className="card col-span-2 sm:col-span-1">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</div>
          <div className={`text-2xl font-bold mt-1 ${isActive ? 'text-coral-700' : 'text-gray-500'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </div>
        </div>
      </div>

      {/* Scorecards */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-gray-900">Scorecards</h2>
          {templates.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-coral-50 text-coral-700 text-xs font-semibold">
              {templates.length} linked
            </span>
          )}
        </div>
        {templates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => {
              const d = details[t];
              return (
                <button
                  key={t}
                  onClick={() => navigate(`/templates/${encodeURIComponent(t)}`)}
                  className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-cloudza-300 hover:bg-cloudza-50/40 text-left transition-colors group"
                >
                  <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-coral-400 to-coral-600 text-white text-xs font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
                    {t.trim().charAt(0)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-gray-800 truncate">{t}</span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      {detailsLoading
                        ? 'Loading…'
                        : d
                          ? `${d.questionCount} question${d.questionCount === 1 ? '' : 's'}${d.language ? ` · ${d.language}` : ''}`
                          : 'View scorecard'}
                    </span>
                  </span>
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-cloudza-500 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="py-10 px-6 text-center">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-coral-50 text-coral-700 flex items-center justify-center">
              <FaClipboardList className="text-lg" />
            </span>
            <p className="mt-4 text-sm font-semibold text-gray-800">No scorecards linked</p>
            <p className="mt-1 text-sm text-gray-400">Edit the campaign to attach the scorecards it should be assessed against.</p>
            <button
              onClick={() => navigate(`/campaigns/${encodeURIComponent(campaign.campaignName)}`)}
              className="btn-outline mt-5"
            >
              <FaPen /> Edit Campaign
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetail;
